import { useMutation } from '@apollo/client';
import { Toggle } from 'components/Toggle';
import { useStore } from 'context/store-context';
import { SAVE_RETURN_WORKFLOW_SETTINGS } from 'gql/mutations';
import { useEffect, useRef, useState } from 'react';
import type { StorePropertiesRefetchResult, StoreValues } from 'types/store';

export const getStoredReturnsEnabled = (
  policySettings?: Record<string, any> | null,
) => policySettings?.returns?.enabled === true;

type SaveRequest = {
  id: number;
  storeId: string;
};

type SaveError = 'mutation' | 'refetch' | null;

type CommittedHydrationBarrier = {
  enabled: boolean;
  staleStoreProperties?: StoreValues;
};

export default function ReturnsProgramStatus() {
  const { storeId, storeProperties, refetchStoreProperties } = useStore();
  const hasCurrentStore = Boolean(storeId && storeProperties?.id === storeId);
  const [saveReturnWorkflowSettings] = useMutation(
    SAVE_RETURN_WORKFLOW_SETTINGS,
  );
  const [enabled, setEnabled] = useState(() =>
    getStoredReturnsEnabled(
      storeId && storeProperties?.id === storeId
        ? storeProperties.policySettings
        : null,
    ),
  );
  const [savingRequest, setSavingRequest] = useState<SaveRequest | null>(null);
  const [saveError, setSaveError] = useState<SaveError>(null);
  const activeStoreIdRef = useRef(storeId);
  const activeStorePropertiesRef = useRef(storeProperties);
  const activeSaveRequestRef = useRef<SaveRequest | null>(null);
  const saveRequestSequenceRef = useRef(0);
  const committedHydrationBarrierByStoreRef = useRef(
    new Map<string, CommittedHydrationBarrier>(),
  );
  const isSaving = Boolean(savingRequest && savingRequest.storeId === storeId);

  useEffect(() => {
    activeStoreIdRef.current = storeId;
    activeStorePropertiesRef.current = storeProperties;
  }, [storeId, storeProperties]);

  useEffect(() => {
    if (isSaving) return;

    if (!hasCurrentStore) {
      setEnabled(false);
      setSaveError(null);
      return;
    }

    const storedEnabled = getStoredReturnsEnabled(
      storeProperties?.policySettings,
    );
    const committedHydrationBarrier = storeId
      ? committedHydrationBarrierByStoreRef.current.get(storeId)
      : undefined;
    if (
      committedHydrationBarrier &&
      committedHydrationBarrier.staleStoreProperties === storeProperties &&
      committedHydrationBarrier.enabled !== storedEnabled
    ) {
      return;
    }
    if (storeId) {
      committedHydrationBarrierByStoreRef.current.delete(storeId);
    }
    setEnabled(storedEnabled);
    setSaveError((currentError) =>
      currentError === 'refetch' ? null : currentError,
    );
  }, [hasCurrentStore, isSaving, storeId, storeProperties]);

  const handleEnabledChange = async (nextEnabled: boolean) => {
    if (!storeId || storeProperties?.id !== storeId) {
      setSaveError('mutation');
      return;
    }

    const previousEnabled = enabled;
    const requestStoreId = storeId;
    const request = {
      id: ++saveRequestSequenceRef.current,
      storeId: requestStoreId,
    };
    activeSaveRequestRef.current = request;
    setEnabled(nextEnabled);
    setSavingRequest(request);
    setSaveError(null);

    const isActiveRequest = () =>
      activeSaveRequestRef.current?.id === request.id &&
      activeStoreIdRef.current === requestStoreId;

    try {
      try {
        await saveReturnWorkflowSettings({
          variables: {
            input: {
              storeId,
              returnsEnabled: nextEnabled,
            },
          },
        });
      } catch (error) {
        if (!isActiveRequest()) return;

        setEnabled(previousEnabled);
        setSaveError('mutation');
        return;
      }

      if (!isActiveRequest()) return;

      committedHydrationBarrierByStoreRef.current.set(requestStoreId, {
        enabled: nextEnabled,
        staleStoreProperties: activeStorePropertiesRef.current,
      });

      let refreshedProperties: StorePropertiesRefetchResult | undefined;
      try {
        refreshedProperties = await refetchStoreProperties?.();
      } catch (error) {
        if (!isActiveRequest()) {
          committedHydrationBarrierByStoreRef.current.delete(requestStoreId);
          return;
        }

        setSaveError('refetch');
        return;
      }

      if (!isActiveRequest()) {
        committedHydrationBarrierByStoreRef.current.delete(requestStoreId);
        return;
      }

      const refreshedStore = refreshedProperties?.data?.store;
      if (refreshedStore?.id === requestStoreId) {
        const refreshedEnabled = getStoredReturnsEnabled(
          refreshedStore.policySettings,
        );
        const currentStoreProperties = activeStorePropertiesRef.current;
        if (
          currentStoreProperties?.id === requestStoreId &&
          getStoredReturnsEnabled(
            currentStoreProperties.policySettings,
          ) === refreshedEnabled
        ) {
          committedHydrationBarrierByStoreRef.current.delete(requestStoreId);
        } else {
          committedHydrationBarrierByStoreRef.current.set(requestStoreId, {
            enabled: refreshedEnabled,
            staleStoreProperties: currentStoreProperties,
          });
        }
        setEnabled(refreshedEnabled);
      }
    } finally {
      if (activeSaveRequestRef.current?.id === request.id) {
        activeSaveRequestRef.current = null;
      }
      setSavingRequest((currentRequest) =>
        currentRequest?.id === request.id ? null : currentRequest,
      );
    }
  };

  return (
    <section className="border-b border-gray-200 pb-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900">
            Enable returns
          </h2>
          {saveError && (
            <p role="alert" className="mt-1 text-xs font-medium text-red-700">
              {saveError === 'refetch'
                ? "Returns were updated, but the latest settings couldn't be loaded. Refresh to confirm."
                : "Couldn't update returns. Try again."}
            </p>
          )}
        </div>
        <Toggle
          isEnabled={enabled}
          onChange={handleEnabledChange}
          rightLabel={isSaving ? 'Saving...' : enabled ? 'On' : 'Off'}
          disabled={isSaving || !hasCurrentStore}
        />
      </div>
    </section>
  );
}
